/**
 * The Files view's in-memory store — one object instead of the module-level
 * singletons that used to live at the top of files_fragment.js. Every module
 * (actions, upload client, body ops, watcher, view) reads and writes `state.*`
 * explicitly, so what's shared is visible and nothing mutates ambient bindings.
 *
 * All of it is per-pane-open and gone when the draft closes; a reopen starts
 * fresh (the staging mirror in sessionData is the only durable part — see
 * persist() and staged_queue.js).
 */

import { setQueue } from "../../js/utils/staged_queue.js";

export const state = {
  // Upload/attach queue: [{ id, name, size, status, percent, oid, content_oid, error }].
  // status ∈ queued | moving | uploading | uploaded | attached | error
  // ("moving" only while an email attachment is being read out of the draft).
  queue: [],
  // File objects for this session's uploads, keyed by entry id (not persistable).
  files: new Map(),
  // True while a send/attach request is in flight — re-entrancy guard and an input
  // to the send button's enabled state.
  sending: false,
  // Recipients (lowercased) currently synced to the transfer — set on send, refreshed
  // after each recipient sync. Lets the watcher skip a no-op sync when the set hasn't
  // changed. null before any send this pane-open.
  sentRecipients: null,
  // The rendered secure-link block from this session's attach, kept in memory so a
  // deleted link can be re-attached while the pane stays open. null until an attach;
  // cleared on a fresh open, on new files, and on Cancel.
  lastAttached: null,
  // The draft's own attachments when they together cross the size threshold — what
  // the "move these to LeapFILE" banner is offering. Empty whenever it's hidden.
  oversizeAttachments: [],
  // True once the user answers the banner this pane-open (moved or dismissed), so a
  // later attachment edit doesn't re-ask.
  attachmentPromptDismissed: false,
  // True while attachments are being moved into the transfer — the removals fire
  // AttachmentsChanged, and the banner must not reappear mid-move.
  movingAttachments: false,
};

/** Stable id for a queue entry (used to key the file Map and the rendered row). */
export function uid() {
  if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
  return "f" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Mirror every staged queue entry (with status) to this item's sessionData so the
 * send flow can gate on midflight / errored uploads. Transient UI fields (percent,
 * error message) are dropped; status + oid are what the send path reasons about.
 * Attached rows are on the sent transfer, not staged — excluding them keeps the
 * reopen-wipe from deleting uploads that are now part of the transfer. Each call
 * writes the FULL current staged queue (not a merge), so callers await it in their
 * sequential flows to keep writes ordered.
 */
export function persist() {
  const entries = state.queue
    .filter((e) => e.status !== "attached")
    .map((e) => ({ id: e.id, name: e.name, size: e.size, status: e.status, oid: e.oid }));
  return setQueue(entries);
}
